import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import localforage from "localforage";
import { JobVisitType, PartnerJobVisitsStorageKey } from "models/JobVisitType";
import { createUpdateVisitVars } from "operations/mutations/UpdateVisit";
import {
  DocumentWebLinkType,
  FileType,
  JobStatus,
  MeterReadingInputType,
  PropertyValueInputType,
  TaskStatus,
  UpdateMachineInputType,
  UploadFileMutationVariables,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { addWebLink, State, storePartnerVisitsToLocal, uploadFile } from "./partner.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  loadPartnerJobVisitsInit: createAppAsyncThunk(
    "partner/loadPartnerJobVisitsInit",
    async (_, { rejectWithValue }) => {
      try {
        const value = await localforage.getItem(PartnerJobVisitsStorageKey);
        return { jobVisits: value };
      } catch (err) {
        return rejectWithValue("unable to load in stored job visits");
      }
    },
  ),
  acceptJob: createAppAsyncThunk(
    "partner/acceptJob",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No selected job");
      const { errors, queued } = await graphqlRequest(sdk.updateJobStatus, {
        thunkName: "partner/acceptJob",
        thunkProps: { ...props },
        variables: {
          jobId,
          status: JobStatus.Accepted,
        },
      });
      if (errors) return rejectWithValue(errors);
      return { queued };
    },
  ),
  uploadFile: createAppAsyncThunk(
    "partner/uploadFile",
    async (props: UploadFileMutationVariables, { rejectWithValue, extra: { sdk } }) => {
      const { jobId, file } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.uploadFile, {
        thunkName: "partner/uploadFile",
        thunkProps: props,
        variables: {
          jobId,
          file: {
            fileGuid: file.fileGuid,
            name: file.name,
            data: file.data,
            description: file.description,
            fileType: file.fileType,
          },
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.uploadFile) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  addWebLink: createAppAsyncThunk(
    "partner/addWebLink",
    async (
      props: { jobId: string; links: DocumentWebLinkType[] },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { jobId, links } = props;
      const mappedLinks = links.map((l) => ({ name: l.name, link: l.link }));
      const { data, errors, queued } = await graphqlRequest(sdk.addDocumentWebLink, {
        thunkName: "visit/addWebLink",
        thunkProps: props,
        variables: {
          jobId,
          links: mappedLinks,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.addDocumentWebLink) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),

  //EditMachineLocationDialog
  updateMachineLocation: createAppAsyncThunk(
    "partner/updateMachineLocation",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineLocation, {
        thunkName: "partner/updateMachineLocation",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineLocation) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  //EditMachineInstallDateDialog
  updateMachineInstallDate: createAppAsyncThunk(
    "partner/updateMachineInstallDate",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineInstallDate, {
        thunkName: "partner/updateMachineInstallDate",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineInstallDate) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  //ChangeJobMachineDialog
  changeJobMachine: createAppAsyncThunk(
    "partner/changeJobMachine",
    async (props: { jobId: string; machineId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.changeJobMachine, {
        thunkName: "partner/changeJobMachine",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.changeJobMachine) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),

  // Visit Specific
  completeVisit: createAppAsyncThunk(
    "partner/completeVisit",
    async (props: { jobId: string }, { getState, dispatch, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        user: { userVar },
        partner: { token, jobVisits },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      if (!jobVisits[jobId] || !token) return rejectWithValue("No job");
      const visit = jobVisits[jobId];
      const completeVisitVars = createUpdateVisitVars(jobId, visit, TaskStatus.Done);

      if (visit.files.length > 0) {
        var failedFiles: FileType[] = [];
        for (const file of visit.files) {
          await dispatch(uploadFile({ jobId, file })).catch(() => {
            failedFiles.push(file);
          });
        }
        if (failedFiles.length > 0) {
          return rejectWithValue({ key: "file-fail", data: failedFiles });
        }
      }

      if (visit.links.length > 0) {
        await dispatch(
          addWebLink({
            jobId,
            links: visit.links,
          }),
        );
      }

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "partner/completeVisit",
        thunkProps: props,
        variables: completeVisitVars,
        onError: [{ type: "partner/resetJobStatus", payload: { jobId } }],
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued, followUp: jobVisits[jobId].followUp.followUpChecked };
    },
  ),
  updateVisit: createAppAsyncThunk(
    "partner/updateVisit",
    async (props: { jobId: string }, { getState, dispatch, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        partner: { jobVisits, token },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      if (!jobVisits[jobId] || !token) return rejectWithValue("No job");
      const visit = jobVisits[jobId];
      const updateVisitVars = createUpdateVisitVars(jobId, visit, TaskStatus.Accepted);

      if (visit.files.length > 0) {
        var failedFiles: FileType[] = [];
        for (const file of visit.files) {
          await dispatch(uploadFile({ jobId, file })).catch(() => {
            failedFiles.push(file);
          });
        }
        if (failedFiles.length > 0) {
          return rejectWithValue({ key: "file-fail", data: failedFiles });
        }
      }

      if (visit.links.length > 0) {
        await dispatch(
          addWebLink({
            jobId,
            links: visit.links,
          }),
        );
      }

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "partner/updateVisit",
        thunkProps: props,
        variables: updateVisitVars,
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),

  updateMachineCustomProps: createAppAsyncThunk(
    "partner/updateMachineCustomProps",
    async (
      props: { properties: PropertyValueInputType[] },
      { getState, rejectWithValue, extra: { sdk } },
    ) => {
      const { properties } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No job");
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineCustomProperties, {
        thunkName: "partner/updateMachineCustomProps",
        thunkProps: props,
        variables: {
          properties,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineCustomProperties)
        return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  updateMeters: createAppAsyncThunk(
    "partner/updateMeters",
    async (
      props: { jobId: string; meters: MeterReadingInputType | MeterReadingInputType[] },
      { getState, rejectWithValue, extra: { sdk } },
    ) => {
      const { jobId, meters } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No job");
      const { data, errors, queued } = await graphqlRequest(sdk.updateMeters, {
        thunkName: "partner/updateMeters",
        thunkProps: props,
        variables: {
          jobId,
          meters,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMeters) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(
    asyncMutations.loadPartnerJobVisitsInit.fulfilled,
    (state, { payload: { jobVisits } }) => {
      state.jobVisits = Object.assign({}, state.jobVisits, jobVisits);
      const now = new Date().toISOString();
      const result = {} as typeof state.jobVisits;

      for (const key in state.jobVisits) {
        if (!state.jobVisits[key].lastUpdated) {
          state.jobVisits[key].lastUpdated = now;
        }

        const lastUpdate = new Date(state.jobVisits[key].lastUpdated!).valueOf();
        const compare = new Date().valueOf() - 14 * 24 * 60 * 60 * 1000;
        // keep newer than 14 days
        if (lastUpdate >= compare) {
          result[key] = state.jobVisits[key];
        }
      }
      state.jobVisits = result;
      storePartnerVisitsToLocal(state);
      return state;
    },
  );
  builder.addCase(asyncMutations.acceptJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.jobs[meta.arg.jobId].status = JobStatus.Downloaded;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Accepted;
    return state;
  });
  // Upload File
  builder.addCase(asyncMutations.uploadFile.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.fileUploading = true;
    return state;
  });
  builder.addCase(asyncMutations.uploadFile.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.fileUploading = false;
  });
  builder.addCase(asyncMutations.uploadFile.fulfilled, (state) => {
    state.fileUploading = false;
    return state;
  });
  // EditMachineLocationDialog
  builder.addCase(asyncMutations.updateMachineLocation.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.location = machine.location;
    }
    return state;
  });
  // EditMachineInstallDateDialog
  builder.addCase(asyncMutations.updateMachineInstallDate.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.installDate = machine.installDate;
    }
    return state;
  });
  // ChangeJobMachineDialog
  builder.addCase(asyncMutations.changeJobMachine.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.changeJobMachine.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.changeJobMachine.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.fulfilled, (state) => {
    state.changeJobMachine.loading = false;
    return state;
  });

  // Visit Specific
  function processUpdateVisitState(jobVisit: JobVisitType, infoString?: string) {
    // Info string contains completed tasks
    if (infoString) {
      const visitUploaded = infoString.includes("CompleteVisit");
      if (visitUploaded) {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.usedParts = [...usedParts];
      } else {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          pendingUpload: p.uploaded,
        }));
        jobVisit.usedParts = [...usedParts];
      }
    }
    jobVisit.files = [];
    jobVisit.links = [];
    return jobVisit;
  }

  builder.addCase(asyncMutations.completeVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    if (typeof errors === "object" && "key" in errors) {
      if (errors.key === "file-fail") {
        const files = errors.data as FileType[];
        state.jobVisits[id].files = files;
      }
    } else {
      const { infoString } = ((
        messages: string[],
      ): { shortMessage: string; infoString?: string } => {
        for (let message of messages) {
          if (!message.includes("|")) continue;
          const [s0] = message.split(" at ");
          const [shortMessage, infoString] = s0.split("|");
          return { shortMessage, infoString };
        }
        return {
          shortMessage: messages[0],
        };
      })(errors.map((e) => e.message));
      state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    }
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog,
    );
    state.token = null;
    return state;
  });

  builder.addCase(asyncMutations.updateVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    if (typeof errors === "object" && "key" in errors) {
      if (errors.key === "file-fail") {
        const files = errors.data as FileType[];
        state.jobVisits[id].files = files;
      }
    } else {
      const { infoString } = ((
        messages: string[],
      ): { shortMessage: string; infoString?: string } => {
        for (let message of messages) {
          if (!message.includes("|")) continue;
          const [s0] = message.split(" at ");
          const [shortMessage, infoString] = s0.split("|");
          return { shortMessage, infoString };
        }
        return {
          shortMessage: messages[0],
        };
      })(errors.map((e) => e.message));

      state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    }
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    let extras = state.jobVisits[id].extras.map((e) => ({
      ...e,
      pendingUpload: true,
    }));
    state.jobVisits[id].extras = [...extras];
    let parts = state.jobVisits[id].usedParts.map((p) => ({
      ...p,
      pendingUpload: true,
    }));
    state.jobVisits[id].usedParts = [...parts];
    let workTimes = state.jobVisits[id].workTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].workTimes = [...workTimes];
    let travelTimes = state.jobVisits[id].travelTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].travelTimes = [...travelTimes];
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog,
    );
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  });

  builder.addCase(asyncMutations.updateMachineCustomProps.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.fulfilled, (state) => {
    state.editMachineLoading = false;
    return state;
  });
};
